import { authenticatedFetch } from '@/lib/auth';

const API_BASE_URL = '/api';

// Interfaces de Tipo
export interface Client {
  id: number;
  client_type: 'pf' | 'pj';
  company_name: string | null;
  trading_name: string | null;
  cnpj: string | null;
  cpf_holder: string | null;
  email: string;
  phone: string | null;
  address_city: string | null;
  address_state: string | null;
  status: 'lead' | 'prospect' | 'active' | 'inactive' | 'suspended';
  priority: 'low' | 'medium' | 'high' | 'vip';
  created_at: string;
  users_count?: number;
}

export interface ClientFormData {
  client_type: 'pf' | 'pj';
  company_name?: string;
  trading_name?: string;
  cnpj?: string;
  cpf_holder?: string;
  email: string;
  phone?: string;
  address_street?: string;
  address_number?: string;
  address_complement?: string;
  address_neighborhood?: string;
  address_city?: string;
  address_state?: string;
  address_zipcode?: string;
  segment?: string;
  status?: string;
  priority?: string;
  notes?: string;
}

export interface User {
  id: number;
  email: string;
  full_name: string;
  user_type: 'internal' | 'client';
  role_id: number;
  role_name?: string;
  client_id: number | null;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  created_at: string;
}

export interface UserFormData {
  email: string;
  password?: string;
  full_name: string;
  user_type: 'internal' | 'client';
  role_id: number;
  client_id?: number | null;
  phone?: string;
  department?: string;
  position?: string;
  status?: string;
}

export interface ClientUser {
  id: number;
  email: string;
  full_name: string;
  role_id: number;
  role_name?: string;
  phone: string | null;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  created_at: string;
}

export interface ListResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Role {
  id: number;
  name: string;
  description: string;
}

export interface PulseSSOResponse {
  success: boolean;
  redirect_url: string;
  token?: string;
}

// Helper para fazer requisições autenticadas
async function apiRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  // Garante que a URL comece com /api se não começar
  const fullUrl = url.startsWith('/api') ? url : `${API_BASE_URL}${url}`;
  
  const response = await authenticatedFetch(fullUrl, options);
  
  // Tenta fazer o parse do JSON, mas trata caso venha vazio ou inválido
  let data;
  try {
    data = await response.json();
  } catch (error) {
    throw new Error('Erro ao processar resposta do servidor');
  }
  
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Erro na requisição');
  }
  
  return data;
}

export const adminService = {
  // ============ SSO ============
  async generatePulseToken(): Promise<PulseSSOResponse> {
    return apiRequest<PulseSSOResponse>('/auth/sso_pulse.php', {
      method: 'POST'
    });
  },

  // ============ CLIENTES ============
  async listClients(search?: string, page = 1, limit = 10): Promise<ListResponse<Client>> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return apiRequest<ListResponse<Client>>(`/admin/clients.php?${params}`);
  },

  async getClient(id: number): Promise<Client> {
    return apiRequest<Client>(`/admin/clients.php?id=${id}`);
  },

  async createClient(data: ClientFormData): Promise<Client> {
    return apiRequest<Client>('/admin/clients.php', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateClient(id: number, data: Partial<ClientFormData>): Promise<Client> {
    return apiRequest<Client>('/admin/clients.php', {
      method: 'PUT',
      body: JSON.stringify({ id, ...data }),
    });
  },

  async deleteClient(id: number): Promise<void> {
    await apiRequest<void>(`/admin/clients.php?id=${id}`, {
      method: 'DELETE',
    });
  },

  // ============ USUÁRIOS DO CLIENTE ============
  async listClientUsers(clientId: number): Promise<ClientUser[]> {
    return apiRequest<ClientUser[]>(`/admin/client-users.php?client_id=${clientId}`);
  },

  async createClientUser(clientId: number, data: { email: string; password: string; full_name: string; role_id: number; phone?: string }): Promise<ClientUser> {
    return apiRequest<ClientUser>('/admin/client-users.php', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, ...data }),
    });
  },

  async updateClientUser(id: number, data: Partial<{ email: string; password: string; full_name: string; role_id: number; phone?: string; status: string }>): Promise<ClientUser> {
    return apiRequest<ClientUser>('/admin/client-users.php', {
      method: 'PUT',
      body: JSON.stringify({ id, ...data }),
    });
  },

  async deleteClientUser(id: number): Promise<void> {
    await apiRequest<void>(`/admin/client-users.php?id=${id}`, {
      method: 'DELETE',
    });
  },

  // ============ USUÁRIOS INTERNOS ============
  async listUsers(search?: string, page = 1, limit = 10): Promise<ListResponse<User>> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return apiRequest<ListResponse<User>>(`/admin/users.php?${params}`);
  },

  async createUser(data: UserFormData): Promise<User> {
    return apiRequest<User>('/admin/users.php', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateUser(id: number, data: Partial<UserFormData>): Promise<User> {
    return apiRequest<User>('/admin/users.php', {
      method: 'PUT',
      body: JSON.stringify({ id, ...data }),
    });
  },

  async deleteUser(id: number): Promise<void> {
    await apiRequest<void>(`/admin/users.php?id=${id}`, {
      method: 'DELETE',
    });
  },

  // ============ ROLES ============
  async listRoles(): Promise<Role[]> {
    return apiRequest<Role[]>('/admin/roles.php');
  },
};